import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useI18nContext } from '@/i18n/i18n-react';
import type {
  CampaignDto,
  TenantHomeResponseDto,
} from '@/generated/korza-api-client/types.gen';
import { useCampaigns } from '@/hooks/use-campaigns';
import { useIsMobile } from '@/hooks/use-mobile';
import { bucketStartIso, type ChartGranularity } from '@/lib/chart-axis';
import { groupCampaignsByBucket } from '@/lib/campaign-markers';
import { resolveHomeHeadline, toHomeChartRows } from '@/lib/home-metrics';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import {
  markPartialRows,
  partialReadoutLabel,
  type ChartRange,
} from '@/lib/partial-buckets';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/components/metrics/formatters';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { HomeCampaignPopover } from '@/components/home/home-campaign-popover';
import { HomeRevenueChart } from '@/components/home/home-revenue-chart';

/** Height of the box the chart fills; the skeleton uses the same. */
const HOME_CHART_HEIGHT = {
  compact: 'h-[120px]',
  full: 'h-[320px]',
} as const;

/** Campaign markers come from one page of the period's sent campaigns. */
const MARKER_CAMPAIGNS_LIMIT = 100;

const NO_CAMPAIGNS = new Map<string, CampaignDto[]>();

export interface HomeRevenueCardProps {
  query: UseQueryResult<TenantHomeResponseDto, Error>;
  /** The filter's period: partial buckets and the campaigns of the markers. */
  range: ChartRange;
  granularity: ChartGranularity;
  /** Label of the selected period (PeriodFilter / DateRangeFilter). */
  periodLabel: string;
  variant: 'compact' | 'full';
  /** GranularityFilter on desktop. */
  headerAction?: ReactNode;
}

/**
 * "Faturamento pelo app" (HOME-03): the period revenue and "{período} · {N}
 * vendas" as the headline, replaced by the point under the finger/mouse
 * ("· parcial" on a partial bucket, CHART-05). A bucket with campaigns opens
 * the card of its campaigns on hover, on drag, or on its marker (HOME-04);
 * on touch it stays open after the finger lifts, until the next touch.
 */
export function HomeRevenueCard({
  query,
  range,
  granularity,
  periodLabel,
  variant,
  headerAction,
}: HomeRevenueCardProps) {
  const { LL, locale } = useI18nContext();
  const isMobile = useIsMobile();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [pinnedIndex, setPinnedIndex] = useState<number | null>(null);
  const data = query.data;

  const campaigns = useCampaigns(
    {
      hasBeenSent: 'true',
      launchDateMin: range.from,
      launchDateMax: range.to,
    },
    MARKER_CAMPAIGNS_LIMIT,
    0
  );

  const rows = useMemo(
    () =>
      data
        ? toHomeChartRows(data.revenueChart.points, granularity, locale)
        : [],
    [data, granularity, locale]
  );
  const partial = usePartialBuckets(rows, range, granularity);
  const chartRows = useMemo(
    () => markPartialRows(rows, 'revenue', partial.edges),
    [rows, partial.edges]
  );
  const groups = useMemo(
    () =>
      campaigns.data
        ? groupCampaignsByBucket(
            campaigns.data.data,
            granularity,
            locale,
            range
          )
        : NO_CAMPAIGNS,
    [campaigns.data, granularity, locale, range]
  );

  const campaignsAt = (index: number | null): CampaignDto[] => {
    const row = index === null ? undefined : rows[index];
    const key = row ? bucketStartIso(row.date, granularity, locale) : null;
    return key ? (groups.get(key) ?? []) : [];
  };

  const handleActiveIndexChange = (index: number | null) => {
    // Touch: the card of the last bucket stays open after the finger lifts.
    // A pinned card closes only on a touch/click outside it or Escape: the
    // chart's hover updates can land after the marker click.
    if (index === null && isMobile && campaignsAt(activeIndex).length > 0) {
      setPinnedIndex(activeIndex);
    }
    setActiveIndex(index);
  };

  // A pinned card closes on the next touch/click outside it, or on Escape.
  useEffect(() => {
    if (pinnedIndex === null) return;
    const close = (event: Event) => {
      if (
        event instanceof KeyboardEvent
          ? event.key === 'Escape'
          : !(
              event.target instanceof Element &&
              event.target.closest('[data-chart-campaign-button]')
            )
      ) {
        setPinnedIndex(null);
      }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [pinnedIndex]);

  const popoverIndex =
    campaignsAt(activeIndex).length > 0 ? activeIndex : pinnedIndex;
  const popoverCampaigns = campaignsAt(popoverIndex);
  const chartHeight = HOME_CHART_HEIGHT[variant];

  const body = () => {
    if (query.isError) {
      return (
        <div
          className={cn(
            'flex flex-col items-start justify-center gap-3',
            chartHeight
          )}
        >
          <p className="text-sm text-muted-foreground">
            {LL.home.revenue.loadError()}
          </p>
          <Button
            type="button"
            variant="outline"
            className="max-md:min-h-touch"
            onClick={() => void query.refetch()}
          >
            {LL.home.revenue.retry()}
          </Button>
        </div>
      );
    }

    if (!data) {
      return (
        <>
          <Skeleton className="h-9 w-40" />
          <Skeleton className="mt-1 h-5 w-28" />
          <Skeleton className={cn('mt-3 w-full', chartHeight)} />
        </>
      );
    }

    const headline = resolveHomeHeadline(data.totalRevenue, rows, activeIndex);
    const activeRow = activeIndex === null ? undefined : chartRows[activeIndex];
    // Point scale: the card follows the bucket, shifted so it stays inside.
    const popoverAt =
      popoverIndex === null || rows.length < 2
        ? 0
        : (popoverIndex / (rows.length - 1)) * 100;
    return (
      <>
        <p
          className="h-9 text-3xl font-bold leading-9 tabular-nums"
          data-headline-kind={headline.kind}
        >
          {formatCurrency(headline.value, locale, data.currency)}
        </p>
        <p className="mt-1 h-5 text-sm text-muted-foreground">
          {headline.kind === 'point'
            ? LL.home.revenue.pointLabel({
                label: partialReadoutLabel(
                  headline.label,
                  activeRow?.partial ?? false,
                  LL
                ),
                count: headline.count,
              })
            : LL.home.revenue.pointLabel({
                label: periodLabel,
                count: data.totalPaidOrders,
              })}
        </p>
        <div className={cn('relative mt-3', chartHeight)}>
          <HomeRevenueChart
            rows={chartRows}
            variant={variant}
            granularity={granularity}
            partial={partial}
            groups={groups}
            onActiveIndexChange={handleActiveIndexChange}
            onMarkerActivate={setPinnedIndex}
          />
          {popoverIndex !== null && popoverCampaigns.length > 0 ? (
            <HomeCampaignPopover
              title={rows[popoverIndex]?.month ?? ''}
              campaigns={popoverCampaigns}
              granularity={granularity}
              currency={data.currency}
              className="absolute top-0 z-30"
              style={{
                left: `${popoverAt}%`,
                transform: `translateX(-${popoverAt}%)`,
              }}
            />
          ) : null}
        </div>
      </>
    );
  };

  return (
    <Card data-home-revenue>
      <CardContent className={cn('p-6', variant === 'compact' && 'max-md:p-4')}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">{LL.home.revenue.label()}</p>
          {headerAction}
        </div>
        <div className="mt-2">{body()}</div>
      </CardContent>
    </Card>
  );
}
